import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";

import { formatYmdShort } from "@/features/marketplace/lib/order-deadline";
import type { AdminOrdersMovementPoint } from "@/features/marketplace/types/marketplace.types";
import type { AdminOrdersMovementSeries } from "./admin-orders-overview";

interface AdminOrdersMovementChartProps {
  points: AdminOrdersMovementPoint[];
  activeSeries?: AdminOrdersMovementSeries;
  activeDate?: string;
  onSelect: (series: AdminOrdersMovementSeries, date: string) => void;
}

const CHART_HEIGHT_PX = 132;
const CREATED_COLOR = "var(--chart-blue)";
const DELIVERED_COLOR = "var(--chart-green)";
const AXIS_TICK = {
  fontSize: 10,
  fill: "var(--muted-foreground)",
} as const;

function MovementTooltip({
  active,
  payload,
  label,
}: TooltipContentProps<number, string>) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length || typeof label !== "string") return null;
  const todayYmd = new Date().toISOString().slice(0, 10);

  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 text-caption shadow-md">
      <p className="mb-1 font-medium text-foreground">
        {formatYmdShort(label, todayYmd, i18n.resolvedLanguage ?? i18n.language)}
      </p>
      <p className="flex min-w-32 items-center gap-2 text-muted-foreground">
        <span
          aria-hidden
          className="size-1.5 rounded-full"
          style={{ backgroundColor: CREATED_COLOR }}
        />
        <span>
          {t(
            "marketplace.ordersDashboard.overview.movement.created",
            "Created",
          )}
        </span>
        <span className="ml-auto tabular-nums text-foreground">
          {payload.find((item) => item.dataKey === "created")?.value ?? 0}
        </span>
      </p>
      <p className="flex min-w-32 items-center gap-2 text-muted-foreground">
        <span
          aria-hidden
          className="size-1.5 rounded-full"
          style={{ backgroundColor: DELIVERED_COLOR }}
        />
        <span>
          {t(
            "marketplace.ordersDashboard.overview.movement.delivered",
            "Delivered",
          )}
        </span>
        <span className="ml-auto tabular-nums text-foreground">
          {payload.find((item) => item.dataKey === "delivered")?.value ?? 0}
        </span>
      </p>
    </div>
  );
}

function MovementDot({
  cx,
  cy,
  point,
  series,
  color,
  isActive,
  ariaLabel,
  onSelect,
}: {
  cx: number;
  cy: number;
  point: AdminOrdersMovementPoint;
  series: AdminOrdersMovementSeries;
  color: string;
  isActive: boolean;
  ariaLabel: string;
  onSelect: (series: AdminOrdersMovementSeries, date: string) => void;
}) {
  const handleKeyDown = (event: KeyboardEvent<SVGCircleElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onSelect(series, point.date);
  };

  return (
    <circle
      cx={cx}
      cy={cy}
      r={isActive ? 4 : 2}
      fill={color}
      stroke={isActive ? "var(--card)" : color}
      strokeWidth={isActive ? 2 : 0}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-pressed={isActive}
      className="cursor-pointer outline-none focus-visible:stroke-[var(--ring)] focus-visible:stroke-[3px]"
      onClick={() => onSelect(series, point.date)}
      onKeyDown={handleKeyDown}
    />
  );
}

export function AdminOrdersMovementChart({
  points,
  activeSeries,
  activeDate,
  onSelect,
}: AdminOrdersMovementChartProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const todayYmd = new Date().toISOString().slice(0, 10);
  const createdTotal = points.reduce((sum, point) => sum + point.created, 0);
  const deliveredTotal = points.reduce(
    (sum, point) => sum + point.delivered,
    0,
  );
  const formatDate = (date: string) =>
    formatYmdShort(date, todayYmd, locale);

  return (
    <section
      aria-labelledby="admin-orders-movement-title"
      className="min-w-0 p-4"
    >
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2
          id="admin-orders-movement-title"
          className="text-xs font-medium text-foreground"
        >
          {t(
            "marketplace.ordersDashboard.overview.movement.title",
            "30-day movement",
          )}
        </h2>
        <span className="inline-flex items-center gap-1.5 text-caption text-muted-foreground">
          <span
            aria-hidden
            className="size-1.5 rounded-full"
            style={{ backgroundColor: CREATED_COLOR }}
          />
          {t(
            "marketplace.ordersDashboard.overview.movement.created",
            "Created",
          )}
        </span>
        <span className="inline-flex items-center gap-1.5 text-caption text-muted-foreground">
          <span
            aria-hidden
            className="size-1.5 rounded-full"
            style={{ backgroundColor: DELIVERED_COLOR }}
          />
          {t(
            "marketplace.ordersDashboard.overview.movement.delivered",
            "Delivered",
          )}
        </span>
      </div>

      <p className="sr-only">
        {t(
          "marketplace.ordersDashboard.overview.movement.summary",
          "{{created}} orders created and {{delivered}} orders delivered over the last 30 days.",
          { created: createdTotal, delivered: deliveredTotal },
        )}
      </p>

      {createdTotal === 0 && deliveredTotal === 0 ? (
        <div
          className="flex items-center justify-center text-xs text-muted-foreground"
          style={{ height: CHART_HEIGHT_PX }}
        >
          {t(
            "marketplace.ordersDashboard.overview.movement.empty",
            "No order movement in this period",
          )}
        </div>
      ) : (
        <div className="mt-2">
          <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX}>
            <LineChart
              data={points}
              margin={{ top: 8, right: 8, bottom: 0, left: -20 }}
            >
              <CartesianGrid
                stroke="var(--border)"
                strokeDasharray="3 3"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={{ stroke: "var(--border)" }}
                minTickGap={28}
                tickFormatter={formatDate}
              />
              <YAxis
                allowDecimals={false}
                tick={AXIS_TICK}
                tickLine={false}
                axisLine={false}
                width={28}
              />
              <Tooltip
                content={(props) => (
                  <MovementTooltip
                    {...(props as TooltipContentProps<number, string>)}
                  />
                )}
                cursor={{ stroke: "var(--border)" }}
                wrapperStyle={{ zIndex: 50 }}
              />
              <Line
                type="monotone"
                dataKey="created"
                stroke={CREATED_COLOR}
                strokeWidth={1.5}
                isAnimationActive={false}
                dot={(props) => {
                  const point = props.payload as AdminOrdersMovementPoint;
                  const isActive =
                    activeSeries === "created" && activeDate === point.date;
                  return (
                    <MovementDot
                      cx={Number(props.cx)}
                      cy={Number(props.cy)}
                      point={point}
                      series="created"
                      color={CREATED_COLOR}
                      isActive={isActive}
                      ariaLabel={t(
                        "marketplace.ordersDashboard.overview.movement.pointAria",
                        "{{series}} on {{date}}: {{count}} orders",
                        {
                          series: t(
                            "marketplace.ordersDashboard.overview.movement.created",
                            "Created",
                          ),
                          date: formatDate(point.date),
                          count: point.created,
                        },
                      )}
                      onSelect={onSelect}
                    />
                  );
                }}
              />
              <Line
                type="monotone"
                dataKey="delivered"
                stroke={DELIVERED_COLOR}
                strokeWidth={1.5}
                isAnimationActive={false}
                dot={(props) => {
                  const point = props.payload as AdminOrdersMovementPoint;
                  const isActive =
                    activeSeries === "delivered" && activeDate === point.date;
                  return (
                    <MovementDot
                      cx={Number(props.cx)}
                      cy={Number(props.cy)}
                      point={point}
                      series="delivered"
                      color={DELIVERED_COLOR}
                      isActive={isActive}
                      ariaLabel={t(
                        "marketplace.ordersDashboard.overview.movement.pointAria",
                        "{{series}} on {{date}}: {{count}} orders",
                        {
                          series: t(
                            "marketplace.ordersDashboard.overview.movement.delivered",
                            "Delivered",
                          ),
                          date: formatDate(point.date),
                          count: point.delivered,
                        },
                      )}
                      onSelect={onSelect}
                    />
                  );
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
